'use client';
import { useBrandedVisitorGate } from '#pplx/frontend/apps/ask/hooks/branded-visitor-gate-hooks/useBrandedVisitorGate.js';
import { useCanSelectSearchModel } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useCanSelectSearchModel.js';
import { useUserCapabilities } from '#pplx/frontend/apps/ask/queries/user-queries/useUserCapabilities.js';
import { useSearchModeForSearchModel } from '#pplx/frontend/apps/ask/search/hooks/useSearchModeForSearchModel.js';
import { useMaxUpsell } from '#pplx/frontend/apps/ask/subscription/hooks/useMaxUpsell.js';
import { useSearchModels } from '#pplx/frontend/ask-models/hooks/useSearchModels.js';
import { SearchMode, SearchModel, SubscriptionTier, } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useIsUserLoggedIn } from '#pplx/frontend/auth/userHooks.js';
import { ModalOrigin } from '#pplx/frontend/components/types/types.js';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
function requiresMaxTier(searchMode) {
    return searchMode === SearchMode.AGENTIC_RESEARCH || searchMode === SearchMode.ASI;
}
/**
 * Handles gating search mode or search model selection
 * with sign in or paywall upsell
 */
export function useGateSearchModelSelection({ openPaywallUpsell, }) {
    const { $t } = useIntl();
    const isUserLoggedIn = useIsUserLoggedIn();
    const { openVisitorLoginUpsell } = useBrandedVisitorGate({
        enabled: !isUserLoggedIn,
    });
    const shouldRenderMaxUpsell = useMaxUpsell();
    const canSelectSearchModel = useCanSelectSearchModel();
    const { specialCapabilities } = useUserCapabilities();
    const { getModelConfig } = useSearchModels({ reason: 'use_gate_search_model_selection' });
    const getSearchModeForSearchModel = useSearchModeForSearchModel();
    /**
     * Gates search mode selection with sign in or paywall upsell
     * Copy reflects search mode that we are upselling to
     */
    const gateSearchModeSelection = useCallback((searchModel, opts) => {
        const { originOverride } = opts ?? {};
        const searchMode = getSearchModeForSearchModel(searchModel);
        if (searchModel === SearchModel.DEFAULT) {
            return false;
        }
        // ASI gating is handled at submit time by useComputerBeforeSubmit
        if (searchMode === SearchMode.ASI) {
            return false;
        }
        const showGate = canSelectSearchModel(searchModel) === false;
        if (!isUserLoggedIn) {
            // Allow visitors with special capability to select Pro without gate
            if (searchModel === SearchModel.PRO && specialCapabilities.unlimitedProSearch) {
                return false;
            }
            const modeToTitle = {
                [SearchMode.SEARCH]: $t({
                    defaultMessage: 'Sign in to access Pro Search',
                    description: 'Visitor login upsell title shown when a logged-out user tries to select Pro Search mode',
                    id: '3b23b6acea',
                }),
                [SearchMode.RESEARCH]: $t({
                    defaultMessage: 'Sign in to access Deep Research',
                    description: 'Visitor login upsell title shown when a logged-out user tries to select Research mode',
                    id: '241e96d301',
                }),
                [SearchMode.STUDY]: $t({
                    defaultMessage: 'Sign in to access Learn mode',
                    description: 'Visitor login upsell title shown when a logged-out user tries to select Study mode',
                    id: '97174d9e2e',
                }),
                [SearchMode.AGENTIC_RESEARCH]: $t({
                    defaultMessage: 'Sign in to access Model Council',
                    description: 'Visitor login upsell title shown when a logged-out user tries to select Agentic Research mode',
                    id: '6a141f8366',
                }),
                [SearchMode.BROWSER_AGENT]: $t({
                    defaultMessage: 'Sign in to let Comet browse the web for you',
                    description: 'Visitor login upsell title shown when a logged-out user tries to select Browser Agent mode',
                    id: '4cf6296196',
                }),
                [SearchMode.ASI]: $t({
                    defaultMessage: 'Sign in to use Computer',
                    description: 'Visitor login upsell title shown when a logged-out user tries to select ASI mode',
                    id: '83b89b0319',
                }),
            };
            const modeToDescription = {
                [SearchMode.SEARCH]: $t({
                    defaultMessage: '10x as many citations in answers',
                    description: 'Visitor login upsell description for Pro Search mode',
                    id: '29d2fd3c5c',
                }),
                [SearchMode.RESEARCH]: $t({
                    defaultMessage: 'Get deep research and analysis on any topic.',
                    description: 'Visitor login upsell description for Research mode',
                    id: '477819cd84',
                }),
                [SearchMode.STUDY]: $t({
                    defaultMessage: 'Get guided explanations, examples, and quizzes tailored to how you learn.',
                    description: 'Visitor login upsell description for Study mode',
                    id: '20186798df',
                }),
                [SearchMode.AGENTIC_RESEARCH]: $t({
                    defaultMessage: 'Compare answers from leading models side-by-side for research, complex decisions, and verification.',
                    description: 'Visitor login upsell description for Agentic Research mode',
                    id: 'f789b325dd',
                }),
                [SearchMode.BROWSER_AGENT]: $t({
                    defaultMessage: 'Autonomously browse the web to complete tasks for you',
                    description: 'Visitor login upsell description for Browser Agent mode',
                    id: '0e508ecc9e',
                }),
                [SearchMode.ASI]: $t({
                    defaultMessage: 'Offload and automate your work.',
                    description: 'Visitor login upsell description for ASI mode',
                    id: '677e863d53',
                }),
            };
            const title = modeToTitle[searchMode ?? SearchMode.SEARCH];
            const description = modeToDescription[searchMode ?? SearchMode.SEARCH];
            openVisitorLoginUpsell({
                title,
                description,
                origin: originOverride ?? ModalOrigin.TRY_PRO,
            });
            return true;
        }
        else if (showGate) {
            const modeToTitle = {
                [SearchMode.SEARCH]: $t({
                    defaultMessage: 'Upgrade for premium AI models and advanced search',
                    description: 'Paywall upsell title shown when a logged-in user tries to select Pro Search mode',
                    id: '0e08dd55dc',
                }),
                [SearchMode.RESEARCH]: $t({
                    defaultMessage: 'Upgrade for advanced research capabilities',
                    description: 'Paywall upsell title shown when a logged-in user tries to select Research mode',
                    id: 'd87806f301',
                }),
                [SearchMode.STUDY]: $t({
                    defaultMessage: 'Step-by-step learning',
                    description: 'Paywall upsell title shown when a logged-in user tries to select Study mode',
                    id: '1ea2c3b78a',
                }),
                [SearchMode.AGENTIC_RESEARCH]: $t({
                    defaultMessage: 'Upgrade to Max to access Model council',
                    description: 'Paywall upsell title shown when a logged-in user tries to select Agentic Research mode',
                    id: 'f9cf7b1e4e',
                }),
                [SearchMode.BROWSER_AGENT]: $t({
                    defaultMessage: 'Let Comet browse the web for you',
                    description: 'Paywall upsell title shown when a logged-in user tries to select Browser Agent mode',
                    id: 'fd609d4b48',
                }),
                [SearchMode.ASI]: $t({
                    defaultMessage: 'Upgrade your plan to use Computer',
                    description: 'Paywall upsell title shown when a logged-in user tries to select Perplexity Computer',
                    id: '519e283b7f',
                }),
            };
            const modeToDescription = {
                [SearchMode.SEARCH]: $t({
                    defaultMessage: 'With Perplexity Pro, get as many as you want',
                    description: 'Paywall upsell description for Pro Search mode encouraging upgrade',
                    id: '88c3423256',
                }),
                [SearchMode.RESEARCH]: shouldRenderMaxUpsell(searchMode)
                    ? $t({
                        defaultMessage: 'Upgrade to Max for enhanced access to Perplexity deep research.',
                        description: 'Paywall upsell description for Research mode when Max tier upsell is shown',
                        id: 'c9058681fa',
                    })
                    : $t({
                        defaultMessage: 'Extended access for subscribers',
                        description: 'Paywall upsell description for Research mode when standard upsell is shown',
                        id: 'ceda45ccae',
                    }),
                [SearchMode.STUDY]: $t({
                    defaultMessage: 'Ask questions and learn',
                    description: 'Paywall upsell description for Study mode',
                    id: '6193252bb9',
                }),
                [SearchMode.AGENTIC_RESEARCH]: $t({
                    defaultMessage: 'Consult multiple AI models at once for research, complex decisions, or verification.',
                    description: 'Paywall upsell description for Agentic Research mode',
                    id: 'bab4452302',
                }),
                [SearchMode.BROWSER_AGENT]: $t({
                    defaultMessage: 'Autonomously browse the web to complete tasks for you',
                    description: 'Paywall upsell description for Browser Agent mode',
                    id: '687288fdcc',
                }),
                [SearchMode.ASI]: shouldRenderMaxUpsell(searchMode)
                    ? $t({
                        defaultMessage: 'Upgrade to Max for enhanced access to Perplexity ASI.',
                        description: 'Paywall upsell description for ASI mode when Max tier upsell is shown',
                        id: 'ce830e89db',
                    })
                    : $t({
                        defaultMessage: 'Extended access for subscribers',
                        description: 'Paywall upsell description for ASI mode when standard upsell is shown',
                        id: '5f8044728f',
                    }),
            };
            const title = modeToTitle[searchMode ?? SearchMode.SEARCH];
            const description = modeToDescription[searchMode ?? SearchMode.SEARCH];
            const modeToOrigin = {
                [SearchMode.SEARCH]: ModalOrigin.TRY_COPILOT,
                [SearchMode.RESEARCH]: ModalOrigin.TRY_RESEARCH,
                [SearchMode.AGENTIC_RESEARCH]: ModalOrigin.TRY_MODEL_COUNCIL,
            };
            const origin = originOverride ??
                modeToOrigin[searchMode ?? SearchMode.SEARCH] ??
                ModalOrigin.TRY_COPILOT;
            const highlightTier = requiresMaxTier(searchMode) || shouldRenderMaxUpsell(searchMode)
                ? SubscriptionTier.MAX
                : undefined;
            openPaywallUpsell({
                pitchMessage: { title, description },
                origin,
                highlightTier,
            });
            return true;
        }
        else {
            return false;
        }
    }, [
        $t,
        canSelectSearchModel,
        isUserLoggedIn,
        openPaywallUpsell,
        openVisitorLoginUpsell,
        shouldRenderMaxUpsell,
        specialCapabilities.unlimitedProSearch,
        getSearchModeForSearchModel,
    ]);
    /**
     * Gates users selecting search model preferences from model selector
     */
    const gateSearchModelSelection = useCallback((searchModel) => {
        // ASI gating is handled at submit time by useComputerBeforeSubmit
        const searchMode = getSearchModeForSearchModel(searchModel);
        if (searchMode === SearchMode.ASI) {
            return false;
        }
        if (canSelectSearchModel(searchModel)) {
            return false;
        }
        const modelConfig = getModelConfig(searchModel);
        const isMaxTierModal = modelConfig?.subscription_tier === 'max';
        if (!isUserLoggedIn) {
            openVisitorLoginUpsell({
                title: $t({
                    defaultMessage: 'Sign in to access the top AI models',
                    description: 'Visitor login upsell title shown when a logged-out user tries to select a specific AI model',
                    id: '411da023b4',
                }),
                description: $t({
                    defaultMessage: 'Switch between the latest models from Perplexity, OpenAI, Anthropic, Google, and more. Sign in or sign up free.',
                    description: 'Visitor login upsell description shown when a logged-out user tries to select a specific AI model',
                    id: 'c45a6ea577',
                }),
                origin: ModalOrigin.MODEL_SELECTOR,
            });
        }
        else {
            openPaywallUpsell({
                pitchMessage: {
                    title: $t({
                        defaultMessage: 'Upgrade to access the latest AI models in one subscription',
                        description: 'Paywall upsell title shown when a logged-in user tries to select a gated AI model',
                        id: '0617bbb1b7',
                    }),
                    description: isMaxTierModal
                        ? $t({
                            defaultMessage: 'With Perplexity Max, you get access to all the latest AI models in one subscription',
                            description: 'Paywall upsell description for Max-tier model selection in the model selector',
                            id: '6f7977340f',
                        })
                        : $t({
                            defaultMessage: 'With Perplexity Pro, you get access to more of the latest AI models in one subscription',
                            description: 'Paywall upsell description for Pro-tier model selection in the model selector',
                            id: 'fe2276e53b',
                        }),
                },
                origin: ModalOrigin.MODEL_SELECTOR,
                highlightTier: isMaxTierModal ? SubscriptionTier.MAX : undefined,
            });
        }
        return true;
    }, [
        canSelectSearchModel,
        getSearchModeForSearchModel,
        isUserLoggedIn,
        openPaywallUpsell,
        openVisitorLoginUpsell,
        $t,
        getModelConfig,
    ]);
    return useMemo(() => ({ gateSearchModeSelection, gateSearchModelSelection }), [gateSearchModeSelection, gateSearchModelSelection]);
}
//# sourceMappingURL=useGateSearchModelSelection.js.map